// features/pricing/components/PricingCard.tsx
import React, { useState } from "react";
import { BillingPeriod, CurrentPlan, PricingPlan } from "../model/PricingModel";
import { Check, Sparkles, Users, UserCheck, Zap } from "lucide-react";

interface PricingCardProps {
  plan: PricingPlan;
  onGetStarted: (
    plan: PricingPlan,
    billingPeriod: BillingPeriod,
    isDowngrade?: boolean,
    isUpgrade?: boolean,
    isBillingSwitch?: boolean,
  ) => void;
  currentPlan?: CurrentPlan;
  showCurrentPlan?: boolean;
}

const formatPrice = (price?: number) => {
  if (typeof price !== "number") {
    return null;
  }
  return `$${Number.isInteger(price) ? price.toFixed(0) : price.toFixed(2)}`;
};

const parsePrice = (price?: string) => {
  if (!price) {
    return undefined;
  }

  const match = price.match(/\$([\d,]+(?:\.\d+)?)/);
  return match ? Number(match[1].replace(/,/g, "")) : undefined;
};

const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  onGetStarted,
  currentPlan,
}) => {
  // Each card manages its own billing period state
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");

  const isPastDue = currentPlan?.subscription_status === "PAST_DUE";
  const isEnterprise =
    plan.tierType === "ENTERPRISE" || plan.name.toLowerCase() === "enterprise";
  const isDisabled = plan.disabled || isEnterprise;
  const isPopular = plan.popular;
  const monthlyPrice = plan.priceMonthly ?? parsePrice(plan.price);
  const yearlyPrice =
    plan.priceYearly ??
    parsePrice(plan.yearlyPrice) ??
    (monthlyPrice ? monthlyPrice * 12 : undefined);
  const hasMonthlyPrice = typeof monthlyPrice === "number";
  const hasYearlyPrice = typeof yearlyPrice === "number" && yearlyPrice > 0;
  const selectedPrice =
    billingPeriod === "yearly"
      ? plan.yearlyPriceDisplay || formatPrice(yearlyPrice)
      : plan.monthlyPriceDisplay || formatPrice(monthlyPrice);
  const priceLabel = isEnterprise ? "Coming Soon" : selectedPrice || plan.price;
  const periodLabel = isEnterprise
    ? ""
    : billingPeriod === "yearly"
    ? plan.yearlyPeriodDisplay || "/year"
    : plan.monthlyPeriodDisplay || "/month";
  const hasYearlySavings =
    billingPeriod === "yearly" &&
    hasMonthlyPrice &&
    hasYearlyPrice &&
    yearlyPrice! < monthlyPrice! * 12;
  const yearlySavings = hasYearlySavings
    ? monthlyPrice! * 12 - yearlyPrice!
    : 0;
  const planOrder: Record<string, number> = {
    starter: 1,
    pro: 2,
    enterprise: 3,
  };

  const currentRank = planOrder[currentPlan?.name?.toLowerCase() || ""] || 0;
  const planRank = planOrder[plan.name.toLowerCase()] || 0;
  const hasCurrentPlan =
    typeof currentPlan?.name === "string" && currentPlan.name.trim() !== "";

  const normalizedCurrentPlan = hasCurrentPlan
    ? currentPlan?.name?.toLowerCase()
    : "";

  const normalizedPlanName = plan.name.toLowerCase();

  const isCurrentPlan =
    hasCurrentPlan && normalizedCurrentPlan === normalizedPlanName;

  const isUpgrade = hasCurrentPlan && planRank > currentRank;

  const isDowngrade = hasCurrentPlan && planRank < currentRank;

  const currentBillingPeriod = currentPlan?.billing_period?.toLowerCase();
  const isSameBillingPeriod = currentBillingPeriod === billingPeriod;
  const isBillingSwitch = isCurrentPlan && !isSameBillingPeriod;

  let dynamicCTA = plan.cta || "Get Started";

  if (currentPlan?.name) {
    if (isCurrentPlan) {
      if (isSameBillingPeriod) {
        dynamicCTA = "Current Plan";
      } else {
        dynamicCTA = billingPeriod === "yearly" ? "Change to Yearly" : "Change to Monthly";
      }
    } else if (isUpgrade) {
      dynamicCTA = "Upgrade";
    } else if (isDowngrade) {
      dynamicCTA = "Downgrade";
    }
  }

  if (isEnterprise) {
    dynamicCTA = "Coming Soon";
  }
  // const isCurrentPlan =
  //   currentPlanName?.toLowerCase() === plan.name.toLowerCase();

  // const isUpgrade = planRank > currentRank;
  // const isDowngrade = planRank < currentRank;

  // let dynamicCTA = plan.cta;

  // if (currentPlanName) {
  //   if (isCurrentPlan) {
  //     dynamicCTA = "Current Plan";
  //   } else if (isUpgrade) {
  //     dynamicCTA = "Upgrade";
  //   } else if (isDowngrade) {
  //     dynamicCTA = "Downgrade";
  //   }
  // }
  return (
    <div
      className={`relative h-full transition-all duration-300 hover:scale-[1.02] ${
        isDisabled ? "opacity-60 grayscale" : ""
      }`}
    >
      {/* Premium Badge with gradient */}
      {isEnterprise ? (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="relative">
            <div className="px-4 py-1.5 bg-gradient-to-r from-gray-400 via-gray-500 to-gray-600 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1.5">
              <span>Coming Soon</span>
            </div>
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 bg-gray-500"></div>
          </div>
        </div>
      ) : (
        (isPopular ||
          (currentPlan?.show_current_plan && plan.name === currentPlan.name)) && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
            <div className="relative">
              <div
                className={`px-4 py-1.5 bg-gradient-to-r text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1.5 ${
                  currentPlan?.show_current_plan && plan.name === currentPlan.name
                    ? "from-emerald-500 via-emerald-600 to-teal-600 shadow-emerald-500/25"
                    : "from-blue-500 via-blue-600 to-indigo-600 shadow-blue-500/25"
                }`}
              >
                <Sparkles size={12} />
                <span>
                  {currentPlan?.show_current_plan &&
                  plan.name === currentPlan.name
                    ? "Current Plan"
                    : "Most Popular"}
                </span>
              </div>
              <div
                className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rotate-45 ${
                  currentPlan?.show_current_plan && plan.name === currentPlan.name
                    ? "bg-emerald-600"
                    : "bg-blue-600"
                }`}
              ></div>
            </div>
          </div>
        )
      )}

      {/* Status Badge */}
      {/* {plan.badge && !isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span
            className={`px-3 py-1 text-xs font-semibold rounded-full border backdrop-blur-sm ${
              plan.badge === "Coming Soon"
                ? "bg-amber-50/80 text-amber-800 border-amber-200"
                : "bg-gray-50/80 text-gray-700 border-gray-200"
            }`}
          >
            {plan.badge}
          </span>
        </div>
      )} */}

      {/* Card Container */}
      <div
        className={`h-full bg-white rounded-2xl border-2 transition-all duration-300 ${
          isDisabled
            ? "border-gray-100 cursor-not-allowed"
            : isPopular
            ? "border-blue-500 shadow-xl shadow-blue-500/10"
            : "border-gray-200 hover:border-blue-300 hover:shadow-lg"
        }`}
      >
        <div className="p-8 flex flex-col h-full">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
              {isPopular && (
                <Zap size={20} className="text-blue-500" fill="#3b82f6" />
              )}
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              {plan.description}
            </p>
          </div>

          {/* Billing Toggle - controls only this card's state */}
          <div className="flex justify-center mb-5">
            <div
              className="inline-grid grid-cols-2 rounded-lg border border-gray-200 bg-gray-50 p-1 shadow-sm"
              role="group"
              aria-label="Billing period"
            >
              {(["monthly", "yearly"] as BillingPeriod[]).map((period) => (
                <button
                  key={period}
                  type="button"
                  onClick={() => setBillingPeriod(period)}
                  className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                    billingPeriod === period
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  aria-pressed={billingPeriod === period}
                >
                  {period === "monthly" ? "Monthly" : "Yearly"}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Section */}
          <div className="mb-8">
            <div className="flex items-baseline mb-2">
              <span className="text-4xl font-bold text-gray-900">
                {priceLabel}
              </span>
              <span className="text-gray-500 ml-2 text-lg">{periodLabel}</span>
            </div>

            {/* Annual Savings */}
            {billingPeriod === "yearly" &&
              (plan.yearlySavingsDisplay || hasYearlySavings) && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-600 text-xs font-bold">
                      %
                    </span>
                  </div>
                  <span className="text-emerald-700 text-sm font-semibold">
                    {plan.yearlySavingsDisplay || (
                      <>
                        Save {formatPrice(yearlySavings)}
                        <span className="text-emerald-600 font-normal ml-1">
                          annually
                        </span>
                      </>
                    )}
                  </span>
                </div>
              )}

            {/* {(plan.trialInfo || plan.trialCredits || plan.monthlyCredits) && (
              <div className="mt-3 space-y-1 text-sm text-gray-600">
                {plan.trialInfo && <div>{plan.trialInfo}</div>}
                {plan.trialCredits ? (
                  <div>{plan.trialCredits} trial credits</div>
                ) : null}
                {plan.monthlyCredits ? (
                  <div>
                    {plan.monthlyCredits.toLocaleString()} credits/month
                  </div>
                ) : null}
              </div>
            )} */}
          </div>

          {/* Usage Limits */}
          {(plan.max_users || plan.max_candidates) && (
            <div className="mb-8 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl border border-gray-200">
              <div className="flex items-center justify-around text-sm">
                {plan.max_users && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Users size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {plan.max_users}
                      </div>
                      <div className="text-gray-500 text-xs">Users</div>
                    </div>
                  </div>
                )}

                {plan.max_candidates && (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <UserCheck size={16} className="text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">
                        {plan.max_candidates}
                      </div>
                      <div className="text-gray-500 text-xs">Candidates</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Features List */}
          <div className="flex-1 mb-8">
            <div className="space-y-3.5">
              {plan.features.slice(0, 6).map((feature, i) => (
                <div key={i} className="flex items-start">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5 ${
                      isDisabled
                        ? "bg-gray-100"
                        : "bg-emerald-50 border border-emerald-100"
                    }`}
                  >
                    <Check
                      size={12}
                      className={
                        isDisabled ? "text-gray-400" : "text-emerald-600"
                      }
                    />
                  </div>
                  <span
                    className={`text-sm ${
                      isDisabled ? "text-gray-400" : "text-gray-700"
                    }`}
                  >
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          {/* CTA Button */}
          <div className="mt-auto">
            {isDisabled || isPastDue || (hasCurrentPlan && isCurrentPlan && isSameBillingPeriod) ? (
              <button
                disabled
                className="w-full py-3.5 px-4 bg-gray-100 text-gray-400 font-semibold text-sm rounded-xl cursor-not-allowed transition-colors"
              >
                {dynamicCTA}
              </button>
            ) : (
              <button
                type="button"
                onClick={() =>
                  onGetStarted(plan, billingPeriod, isDowngrade, isUpgrade, isBillingSwitch)
                }
                className={`group relative w-full inline-flex items-center justify-center py-3.5 px-4 text-sm font-semibold rounded-xl transition-all duration-200 overflow-hidden text-white shadow-lg ${
                  isUpgrade
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-blue-500/25"
                    : isDowngrade
                    ? "bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 shadow-gray-500/25"
                    : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-blue-500/25"
                }`}
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-white/10 via-white/30 to-white/10 group-hover:animate-shine" />

                <span className="relative">{dynamicCTA}</span>

                {!isCurrentPlan && (
                  <svg
                    className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>
          {/* <div className="mt-auto">
            {isDisabled || isCurrentPlan ? (
              <button
                disabled
                className="w-full py-3.5 px-4 bg-gray-100 text-gray-400 font-semibold text-sm rounded-xl cursor-not-allowed transition-colors"
              >
                {dynamicCTA}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onGetStarted(plan, billingPeriod)}
                className="group relative w-full inline-flex items-center justify-center py-3.5 px-4 text-sm font-semibold rounded-xl transition-all duration-200 overflow-hidden bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg shadow-blue-500/25"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-white/10 via-white/30 to-white/10 group-hover:animate-shine" />
                <span className="relative">{dynamicCTA}</span>
                <svg
                  className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </button>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default PricingCard;
